// 보호 경로 공통 레이아웃 — 상단바 + 사이드바 + 콘텐츠.
// 모바일: 햄버거 버튼으로 사이드바 토글. 라우트 변경 시 자동 닫힘.
// 우측: DetailPanel 고정 컬럼 (?item= 기반). Explorer(/)에서는 미선택 시 빈 상태 안내.

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { useToast } from './Toast';
import { Sidebar } from './Sidebar';
import { NotificationBell } from './NotificationBell';
import { useIdleLogout } from '../hooks/useIdleLogout';
import { useDetailPanel } from '../hooks/useDetailPanel';
import { DetailPanel } from './DetailPanel/DetailPanel';
import { NewItemPanel } from './NewItemPanel';
import { setLogoutReason } from '../utils/logoutReason';

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const { itemId, close, panelWidth, setPanelWidth } = useDetailPanel();
  // Explorer(/)에서는 항목 미선택이어도 우측 컬럼(빈 상태)을 유지
  const isExplorer = location.pathname === '/';
  // ?new={type} — 신규 등록 중이면 우측 컬럼을 등록 폼이 차지
  const [searchParams] = useSearchParams();
  const isCreating = searchParams.get('new') !== null;

  // 30분 유휴 시 자동 로그아웃 + /lock 리다이렉트 + 배너 사유 기록
  useIdleLogout();

  // 라우트 변경 시 모바일 사이드바 자동 닫힘
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.search]);

  // ESC로 사이드바 닫기
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      /* 이미 만료돼도 화면은 잠그도록 진행 */
    }
    qc.clear();
    setLogoutReason('manual');
    toast.info('로그아웃되었습니다');
    // hard reload — React Query 캐시·observer 전부 초기화, 무한 스피너 방지
    window.location.replace('/lock');
  };

  return (
    <div className={`layout ${menuOpen ? 'layout--menu-open' : ''}`}>
      <header className="topbar">
        <button
          type="button"
          className="topbar__hamburger"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="메뉴 열기"
          aria-expanded={menuOpen}
        >
          ☰
        </button>
        <NavLink to="/" className="topbar__brand">🔒 Locker</NavLink>
        <div className="topbar__right">
          <NotificationBell />
          <button type="button" className="btn btn--ghost" onClick={handleLogout}>
            로그아웃
          </button>
        </div>
      </header>
      <div className="layout__body">
        <div className="sidebar__backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />
        <Sidebar />
        <main className="main">
          <Outlet />
        </main>
        {isCreating && <NewItemPanel panelWidth={panelWidth} />}
        {!isCreating && (itemId !== null || isExplorer) && (
          <DetailPanel
            itemId={itemId}
            onClose={close}
            panelWidth={panelWidth}
            onWidthChange={setPanelWidth}
            showEmpty={isExplorer}
          />
        )}
      </div>
    </div>
  );
}
